/**
 * Ik Onkar designs — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_kartik`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const ikOnkarDesignsPack: ToilePack = {
    slug: 'ik-onkar-designs',
    category: 'faith',
    name: 'Ik Onkar designs',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'plain', label: 'Plain symbol', subject: 'the Gurmukhi Ik Onkar symbol alone, drawn with balanced strokes and the long arc over the top, centred on a plain ground'},
        {key: 'khanda', label: 'With Khanda', subject: 'the Ik Onkar symbol set inside a circular frame beside a small Khanda, with a thin double ring around both'},
        {key: 'floral', label: 'Floral frame', subject: 'the Ik Onkar symbol inside a round wreath of lotus and marigold petals, with small dots between the petals'},
    ],
    styles: [
        {key: 'brush', label: 'Calligraphic', style: 'calligraphic brush strokes with thick-to-thin pressure and clean tapered ends, a hand-lettered Gurmukhi feel', palette: 'Black ink on a white background.', framing: 'Centered with a wide white margin.'},
        {key: 'stencil', label: 'Cut stencil', style: 'single-piece cut stencil, solid shapes with narrow bridges holding the inner parts, hard edges, no outlines', palette: 'Black on a white background, one color only.', framing: 'Centered and filling most of the square with an even margin.'},
        {key: 'gold', label: 'Gold on dark', style: 'flat gold emblem with a slim lighter edge, smooth gradient-free fill, an even weight in every stroke', palette: 'Metallic gold on a solid deep navy background.', framing: 'Centered in the square, the dark ground running edge to edge.'},
    ],
    directions: ['tattoo', 'saffron', 'small'],
    directionHint: 'try: tattoo',
    seo: {
        title: 'Ik Onkar design: symbol, Khanda and floral frame',
        metaDescription: 'Nine Ik Onkar designs on plain grounds, the symbol alone, in a Khanda or floral frame, as brush, stencil or gold emblem.',
        h1: 'Ik Onkar designs: symbol alone, with Khanda, in a floral frame',
        tagline: 'Nine flat files, the Ik Onkar in three frames and three hands, ready to cut or print.',
        intro: 'The Ik Onkar, the opening of the Mool Mantar, as people use it through the year: a wall decal, a phone wallpaper, a stencil, a card or a tattoo design. Each design shows the symbol alone, inside a circle with a small Khanda, or in a wreath of lotus and marigold petals, as brush calligraphy, a stencil or a gold emblem. Type a different frame or colour, such as saffron, and the same treatments will carry it.',
        useCases: [
            'a wall decal for a home or gurdwara room',
            'a phone wallpaper',
            'a stencil for a painted wall or a cake',
            'a tattoo design to show an artist',
        ],
        faq: [
            {q: 'Can I cut a stencil from these?', a: 'Yes. The cut stencil is one solid piece with narrow bridges holding the inner parts, so it can be cut out as drawn.'},
            {q: 'Are these photographs of a real object?', a: 'No. They are flat drawn designs, not photographs of a wall, a body or a physical object.'},
            {q: 'Which one works as a wallpaper?', a: 'The gold on dark version has a solid navy ground that runs edge to edge, so it works as a full background.'},
            {q: 'Can I ask for a different colour or frame?', a: 'Yes. A direction such as tattoo or saffron is a request for the design, not a promised result.'},
        ],
    },
}
